/**
 * Asking the browser to keep this device's data.
 *
 * Everything the till saves while offline - the product list, and above all the
 * sales waiting to upload - lives in the browser's own storage. By default the
 * browser treats that as a cache: when the disk fills up it may throw it away
 * without asking. "Persistent" storage is the browser's promise not to do that.
 *
 * Chrome grants it silently to a site that is installed or used a lot; Firefox
 * asks the person. Either way the answer must be CHECKED, not assumed - if it
 * is "no", the owner should hear about it while setting the till up, not on the
 * day a sale goes missing.
 */
import { getMeta, setMeta } from './db.js';

export type PersistState = 'granted' | 'denied' | 'unsupported';

const META_KEY = 'storage.persist';

/** Ask once, or ask again if the answer last time was no. Never throws. */
export async function ensurePersistentStorage(): Promise<PersistState> {
  const storage = navigator.storage;
  if (!storage?.persist || !storage.persisted) return 'unsupported';
  try {
    if (await storage.persisted()) return remember('granted');
    return remember((await storage.persist()) ? 'granted' : 'denied');
  } catch {
    return remember('denied');
  }
}

/** What the browser said last time, without asking again. */
export async function lastPersistState(): Promise<PersistState | null> {
  return getMeta<PersistState>(META_KEY);
}

async function remember(state: PersistState): Promise<PersistState> {
  await setMeta(META_KEY, state).catch(() => undefined);
  return state;
}

/**
 * Can this browser ever say yes?
 *
 * Safari on iPhone and iPad implements the ASKING part and then always answers
 * no - there is no gesture, setting or install that changes it. Offering an
 * "Ask again" button there is offering a button that cannot work, which is
 * worse than offering nothing: it reads as broken.
 *
 * iPadOS reports itself as a Mac, so the touch count is what separates an iPad
 * from a real desktop Safari.
 */
export function persistIsGrantable(): boolean {
  const ua = navigator.userAgent || '';
  const isIos =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return !isIos;
}
